import * as Phaser from "phaser"
import { GAME_WIDTH } from "../../data/constants"
import type { EnemyData, GameSprite } from "../../types"
import {
  type EnemyState,
  type EnemyStateContext,
  StatefulEnemyRuntime,
} from "../enemy-system"

const POD_STANDOFF_X = GAME_WIDTH * 0.68
const POD_APPROACH_SPEED = 1.4
const POD_PULSE_MS = 760
const POD_RECOIL_MS = 280

type BioPodPhase = "approach" | "pulsing" | "recoiling"

export class BioPodEnemy extends StatefulEnemyRuntime<BioPodPhase> {
  public constructor(sprite: GameSprite, enemyData: EnemyData) {
    const states: EnemyState<BioPodPhase>[] = [
      {
        name: "approach",
        behaviorState: "tracking",
        animationState: "flying",
        update: (context) => this.updateApproach(context),
      },
      {
        name: "pulsing",
        behaviorState: "focusing",
        animationState: "focusing",
        canFire: true,
        enter: (context) => {
          context.enemyData.lastShot = context.enemyData.shootCooldown * 0.65
        },
        update: (context) => this.updatePulsing(context),
      },
      {
        name: "recoiling",
        behaviorState: "retreating",
        animationState: "retreating",
        canFire: false,
        enter: (context) => {
          context.sprite.setTint(0xc084fc)
          context.vfx.emitImpactBurst(
            context.sprite.x,
            context.sprite.y,
            0xa855f7,
            0.7,
          )
        },
        update: (context) => this.updateRecoiling(context),
      },
    ]

    super(sprite, enemyData, "approach", states)
  }

  private updateApproach(
    context: EnemyStateContext<BioPodPhase>,
  ): BioPodPhase | null {
    this.applyOrganicFloat(context)
    context.enemyData.vx = context.scrollCompensation - POD_APPROACH_SPEED

    if (context.sprite.x <= POD_STANDOFF_X) {
      return "pulsing"
    }

    return null
  }

  private updatePulsing(
    context: EnemyStateContext<BioPodPhase>,
  ): BioPodPhase | null {
    this.applyOrganicFloat(context)
    context.enemyData.vx = context.scrollCompensation

    const pulse = 0.5 + Math.sin(context.stateTime / 70) * 0.5
    context.sprite.setTint(pulse > 0.45 ? 0xa855f7 : 0x8b5cf6)

    if (context.stateTime >= POD_PULSE_MS) {
      return "recoiling"
    }

    return null
  }

  private updateRecoiling(
    context: EnemyStateContext<BioPodPhase>,
  ): BioPodPhase | null {
    this.applyOrganicFloat(context)
    context.enemyData.vx = context.scrollCompensation + 0.85

    if (context.stateTime >= POD_RECOIL_MS) {
      context.sprite.clearTint()
      return "pulsing"
    }

    return null
  }

  private applyOrganicFloat(context: EnemyStateContext<BioPodPhase>): void {
    const { sprite, enemyData, time } = context
    const spec = context.enemyDef?.movementSpec
    const homeY = enemyData.homeY ?? sprite.y

    if (spec?.action !== "sine") return

    const main = Math.sin(time / spec.divisor) * spec.amplitude
    const harmonic =
      Math.sin(time / (spec.divisor * 0.48)) * spec.amplitude * 0.18
    const playerPull = Phaser.Math.Clamp(
      (context.playerY - sprite.y) * 0.018,
      -0.8,
      0.8,
    )
    enemyData.vy = (homeY + main + harmonic - sprite.y) * 0.08 + playerPull
  }
}
